import React, { useEffect, useState, useCallback } from "react";
import { toast, Toaster } from "sonner";
import { CheckCircle2, XCircle, MessageSquare } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Pill, TextArea, Select } from "../../components/common/Primitives";
import { Modal, ModalFooter } from "../../components/common/Modal";
import api, { formatApiError } from "../../lib/api";

const FIELD_LABEL = {
  name: "Full Name",
  dob: "Date of Birth",
  gender: "Gender",
  pan: "PAN",
  email: "Email",
};

export default function AdminProfileRequests() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("Pending");
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null); // { req, action }

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/profile-change-requests?status=${status}`)
      .then((r) => setRows(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardShell title="Profile Change Requests" subtitle="ADMIN · IAM">
      <Toaster richColors position="top-right" />
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-ink-muted">
          Users can request changes to KYC-locked fields (name, DOB, gender, PAN) or their email. Review and approve to update the user record directly.
        </div>
        <div className="w-40">
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} testId="pcr-status-filter">
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Cancelled">Cancelled</option>
            <option value="all">All</option>
          </Select>
        </div>
      </div>
      <div className="border border-white/10 bg-white/70 backdrop-blur overflow-x-auto rounded-md">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-ink-muted">
            <tr className="border-b border-navy/10">
              <th className="text-left px-4 py-3">Requester</th>
              <th className="text-left px-4 py-3">Field</th>
              <th className="text-left px-4 py-3">From</th>
              <th className="text-left px-4 py-3">To</th>
              <th className="text-left px-4 py-3">Reason</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Requested</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="px-4 py-6 text-center text-ink-muted">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="8" className="px-4 py-6 text-center text-ink-muted">No requests.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.request_id} className="border-b border-navy/5 hover:bg-navy/[0.02]" data-testid={`pcr-row-${r.request_id}`}>
                <td className="px-4 py-3">
                  <div className="text-ink font-medium">{r.name || "—"}</div>
                  <div className="text-xs text-ink-muted">{r.email} · <span className="font-mono">{r.role}</span></div>
                </td>
                <td className="px-4 py-3 text-xs font-mono uppercase">{FIELD_LABEL[r.field] || r.field}</td>
                <td className="px-4 py-3 text-xs font-mono text-ink-soft truncate max-w-[140px]" title={r.current_value}>{r.current_value || "—"}</td>
                <td className="px-4 py-3 text-xs font-mono text-ink truncate max-w-[160px]" title={r.new_value}>{r.new_value}</td>
                <td className="px-4 py-3 text-xs text-ink-soft truncate max-w-[180px]" title={r.reason}>{r.reason || "—"}</td>
                <td className="px-4 py-3">
                  <Pill tone={
                    r.status === "Approved" ? "success"
                      : r.status === "Rejected" ? "danger"
                      : r.status === "Cancelled" ? "muted"
                      : "warn"
                  }>{r.status}</Pill>
                </td>
                <td className="px-4 py-3 text-xs text-ink-muted font-mono">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {r.status === "Pending" && (
                      <>
                        <button
                          className="text-emerald-700 hover:bg-emerald-50 p-1.5 rounded"
                          title="Approve"
                          onClick={() => setReviewing({ req: r, action: "approve" })}
                          data-testid={`pcr-approve-${r.request_id}`}
                        ><CheckCircle2 size={16} /></button>
                        <button
                          className="text-rose-600 hover:bg-rose-50 p-1.5 rounded"
                          title="Reject"
                          onClick={() => setReviewing({ req: r, action: "reject" })}
                          data-testid={`pcr-reject-${r.request_id}`}
                        ><XCircle size={16} /></button>
                      </>
                    )}
                    {r.review_note && (
                      <span title={`Reviewer: ${r.review_note}`}>
                        <MessageSquare size={13} className="text-ink-muted" />
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {reviewing && (
        <ReviewModal
          {...reviewing}
          onClose={() => setReviewing(null)}
          onDone={() => { setReviewing(null); load(); }}
        />
      )}
    </DashboardShell>
  );
}

function ReviewModal({ req, action, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/profile-change-requests/${req.request_id}/${action}`, { note });
      toast.success(action === "approve" ? "Request approved — user profile updated" : "Request rejected");
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  const isApprove = action === "approve";
  return (
    <Modal open onClose={onClose} title={isApprove ? "Approve change" : "Reject change"} subtitle={`${req.email} · ${FIELD_LABEL[req.field] || req.field}`} size="md" testId="pcr-review-modal">
      <form onSubmit={submit} className="space-y-3">
        <div className="text-xs text-ink-muted">
          {isApprove ? "This will update the user's profile immediately." : "The user will see this rejection in their profile page."}
        </div>
        <div className="grid grid-cols-2 gap-3 border border-navy/10 rounded-md p-3 bg-navy/[0.02]">
          <div>
            <div className="overline mb-1">From</div>
            <div className="text-sm font-mono text-ink-muted truncate">{req.current_value || "—"}</div>
          </div>
          <div>
            <div className="overline mb-1">To</div>
            <div className="text-sm font-mono text-ink truncate">{req.new_value}</div>
          </div>
        </div>
        {req.reason && (
          <div className="text-xs text-ink-soft italic border-l-2 border-navy/20 pl-3">{req.reason}</div>
        )}
        <TextArea
          label="Review note (visible to the user)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          testId="pcr-review-note"
        />
        <ModalFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant={isApprove ? "gold" : "danger"} disabled={saving} data-testid="pcr-review-submit">
            {saving ? "Saving…" : isApprove ? "Approve & apply" : "Reject"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
